import { useEffect, useState } from "react";
import { Compass } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { GrowthView } from "@/components/assessment/GrowthView";

const LV = { below: "Below grade", on: "On grade", above: "Above grade" };
const STYLE = { see: "Visual", hear: "Listening", do: "Hands-on", read: "Reading/writing" };

export const AssessmentSummary = ({ studentId, editable = true }) => {
  const [d, setD] = useState(null);
  const load = () => http.get(`/assessment/students/${studentId}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const a = d.assessment;
  const ov = a?.overrides || {};
  const save = async (patch) => {
    try { setD({ ...d, assessment: (await http.put(`/assessment/students/${studentId}/override`, { levels: ov.levels || {}, style: ov.style, pace: ov.pace, ...patch })).data }); toast.success("Learning profile updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="mt-6 rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6" data-testid="assessment-summary">
      <p className="font-display text-xl text-[#2C4A3E] inline-flex items-center gap-2"><Compass className="w-5 h-5 text-[#C87D53]" /> Start-of-year check-in · {d.school_year}</p>
      {!a ? <p className="text-sm text-stone-600 mt-2" data-testid="assessment-status">Not started yet.</p> : (
        <>
          <p className="text-sm text-stone-600 mt-1" data-testid="assessment-status">{a.status === "done" ? `Completed ${a.completed_at.slice(0, 10)}` : "In progress"}
            {a.style && ` · Learns best: ${STYLE[ov.style || a.style.learn_best]} · Pace: ${ov.pace || a.style.pace}`}{a.style?.interests?.length > 0 && ` · Loves ${a.style.interests.join(", ")}`}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">{Object.entries(a.subjects).map(([sid, s]) => {
            const lvl = ov.levels?.[sid] || s.level;
            return (
              <div key={sid} className="rounded-2xl bg-white border border-[#E6DEC3] p-4" data-testid={`assessment-subject-${sid}`}>
                <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-[#2C4A3E]">{s.name}</p>
                  {editable && s.level ? (
                    <select value={lvl} onChange={(e) => save({ levels: { ...(ov.levels || {}), [sid]: e.target.value } })} data-testid={`assessment-override-${sid}`} className="text-xs rounded-full border border-[#E6DEC3] px-2 py-1 bg-[#FDFBF7]">
                      {Object.entries(LV).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  ) : <span className="text-xs text-stone-500">{lvl ? LV[lvl] : `${s.answered}/${s.total}`}</span>}
                </div>
                {s.level && <p className="text-xs text-stone-500 mt-1">Score {s.score}/{s.total}{ov.levels?.[sid] && " · adjusted"}{s.weak.length > 0 && ` · Review: ${s.weak.join(", ")}`}</p>}
              </div>
            );
          })}</div>
          {ov.by && <p className="text-xs text-stone-400 mt-3">Adjusted by {ov.by}</p>}
          {d.pending === "winter" && <p className="text-xs text-[#C87D53] mt-3" data-testid="assessment-midyear-pending">Mid-year check-in is open and not finished yet.</p>}
          <GrowthView growth={d.growth} testId="assessment-growth" />
        </>
      )}
    </section>
  );
};
